import { Container } from 'unstated';
import * as voicifyApi from '../../api';
import { isAbsolute } from 'path';
import AuthenticationResponse from '../../models/auth/api/AuthenticationResponse';
import { VOICIFY_ACCESS_TOKEN_STORE, VOICIFY_REFRESH_TOKEN_STORE, VOICIFY_EMAIL_STORE, VOICIFY_ACCESS_EXPIRATION, VOICIFY_REFRESH_EXPIRATION, VOICIFY_API_KEY } from '../../constants/Keys';
import IResult from '../../models/result/IResult';
import SingleSignOnResponse from '../../models/auth/api/SingleSignOnResponse';
import SingleSignOnRequest from '../../models/auth/api/SingleSignOnRequest';

type AuthState = {
    isAuthenticated: boolean
    accessToken: string
    refreshToken: string
    expirationDate: Date
    refreshTokenExpirationDate: Date
    errors: string[]
    isLoading: boolean,
    apiKeyAuth: boolean,
}

export default class AuthContainer extends Container<AuthState> {
    state = this.getInitialState()
    timerTaskId;
    storageListener: (event: StorageEvent) => void;

    constructor() {
        super();
        // If we have a refresh token on initialization, start the refresh timer
        if (this.state.isAuthenticated && this.state.refreshToken) {
            this.setRefreshTimer();
        }
    }

    getInitialState(): AuthState {
        // check local storage for initial state
        localStorage.removeItem(VOICIFY_ACCESS_TOKEN_STORE); // remove any history of storing access in local storage
        const storedAccessToken = sessionStorage.getItem(VOICIFY_ACCESS_TOKEN_STORE);
        const storedRefreshToken = localStorage.getItem(VOICIFY_REFRESH_TOKEN_STORE);
        const storedExpiration = localStorage.getItem(VOICIFY_ACCESS_EXPIRATION);
        const storedRefreshExpiration = localStorage.getItem(VOICIFY_REFRESH_EXPIRATION);
        if (storedRefreshToken !== null && storedRefreshToken?.length) {
            return {
                isAuthenticated: true,
                accessToken: storedAccessToken,
                refreshToken: storedRefreshToken,
                expirationDate: new Date(storedExpiration),
                refreshTokenExpirationDate: new Date(storedRefreshExpiration),
                errors: [],
                isLoading: false,
                apiKeyAuth: false
            }
        }

        return {
            isAuthenticated: false,
            accessToken: '',
            refreshToken: '',
            errors: [],
            expirationDate: null,
            refreshTokenExpirationDate: null,
            isLoading: false,
            apiKeyAuth: false
        }
    }

    beginSingleSignOnSignIn(ssoRequest: SingleSignOnRequest): Promise<IResult<SingleSignOnResponse>> {
        this.setState({
            ...this.state,
            isLoading: true
        })
        return this.beginSingleSignOn(voicifyApi.beginSingleSignOnSignIn(ssoRequest));
    }

    beginSingleSignOnSignUp(ssoRequest: SingleSignOnRequest): Promise<IResult<SingleSignOnResponse>> {
        this.setState({
            ...this.state,
            isLoading: true
        })
        return this.beginSingleSignOn(voicifyApi.beginSingleSignOnSignUp(ssoRequest));
    }


    beginSingleSignOn(promise: Promise<IResult<SingleSignOnResponse>>): Promise<IResult<SingleSignOnResponse>> {
        promise.then((ssoResult) => {
            if (ssoResult.resultType == "Ok") {
                window.location.href = ssoResult.data.redirectUrl;
            }
            else {
                this.setState({
                    ...this.state,
                    errors: ssoResult.errors,
                    isLoading: false
                })
            }
        }).catch(error => {
            this.setState({
                ...this.state,
                errors: [...error],
                isLoading: false
            });

        });
        return promise;
    }

    updateLocalStorageAuth(email: string, authResult: AuthenticationResponse) {
        sessionStorage.setItem(VOICIFY_ACCESS_TOKEN_STORE, authResult.accessToken);
        localStorage.setItem(VOICIFY_REFRESH_TOKEN_STORE, authResult.refreshToken);
        localStorage.setItem(VOICIFY_EMAIL_STORE, email);
        localStorage.setItem(VOICIFY_ACCESS_EXPIRATION, authResult.accessTokenExpirationDate);
        localStorage.setItem(VOICIFY_REFRESH_EXPIRATION, authResult.refreshTokenExpirationDate);
        this.setRefreshTimer();
    }

    updateAuthWithAPIKey(apiKey: string) {
        sessionStorage.setItem(VOICIFY_API_KEY, apiKey);
        sessionStorage.setItem(VOICIFY_ACCESS_TOKEN_STORE, "");
        localStorage.setItem(VOICIFY_REFRESH_TOKEN_STORE, "");
        localStorage.setItem(VOICIFY_EMAIL_STORE, "");
        localStorage.setItem(VOICIFY_ACCESS_EXPIRATION, "");
        this.setState({
            ...this.state,
            isAuthenticated: true,
            apiKeyAuth: true
        });
    }

    signInWithSingleSignOn(email: string, refreshToken: string): Promise<IResult<AuthenticationResponse>> {
        this.setState({
            ...this.state,
            isLoading: true
        })
        var promise =
            voicifyApi.refreshToken({
                email,
                refreshToken
            });
        promise.then((authResult) => {

            if (authResult.resultType == "Ok") {
                this.setState({
                    ...this.state,
                    isAuthenticated: true,
                    accessToken: authResult.data.accessToken,
                    refreshToken: authResult.data.refreshToken,
                    expirationDate: new Date(authResult.data.accessTokenExpirationDate),
                    refreshTokenExpirationDate: new Date(authResult.data.refreshTokenExpirationDate),
                    errors: [],
                    isLoading: false
                });
                this.updateLocalStorageAuth(email, authResult.data);
            }
            else {
                this.setState({
                    ...this.state,
                    errors: authResult.errors,
                    isLoading: false
                });
                this.signOut();
                window.location.href = `${window.location.origin.toString()}/login`;
            }
        }).catch(error => {
            this.setState({
                ...this.state,
                errors: [...error],
                isLoading: false
            });

        });
        return promise;
    }

    addError(error: string) {
        this.setState({
            ...this.state,
            errors: [...this.state.errors, error],
        });
    }
    clearErrors() {
        this.setState({
            ...this.state,
            errors: [],
        });
    }

    signIn(email: string, password: string): Promise<IResult<AuthenticationResponse>> {
        this.setState({
            ...this.state,
            isLoading: true
        })
        var promise = voicifyApi.signIn({
            email,
            password
        });
        promise.then((authResult) => {

            if (authResult.resultType == "Ok") {
                this.setState({
                    ...this.state,
                    isAuthenticated: true,
                    accessToken: authResult.data.accessToken,
                    refreshToken: authResult.data.refreshToken,
                    expirationDate: new Date(authResult.data.accessTokenExpirationDate),
                    refreshTokenExpirationDate: new Date(authResult.data.refreshTokenExpirationDate),
                    errors: [],
                    isLoading: false
                });
                this.updateLocalStorageAuth(email, authResult.data);
            }
            else {
                this.setState({
                    ...this.state,
                    errors: authResult.errors,
                    isLoading: false
                })
            }
        }).catch(error => {
            this.setState({
                ...this.state,
                errors: [...error],
                isLoading: false
            });

        });
        return promise;
    }
    checkRefreshTokenExpiry() {
        // Always read from localStorage to get the latest token expiration
        // This ensures we don't use stale state if another tab refreshed the token
        const storedRefreshExpiration = localStorage.getItem(VOICIFY_REFRESH_EXPIRATION);
        if (!storedRefreshExpiration) return;

        const refreshExpiration = new Date(storedRefreshExpiration);
        const now = new Date();
        const timeUntilExpiry = refreshExpiration.getTime() - now.getTime();
        const thirtyMinutesInMs = 30 * 60 * 1000;

        if (timeUntilExpiry <= 0) {
            // Refresh token is expired, logout
            this.signOut();
            const fullPath = window.location.pathname + window.location.search;
            if (fullPath.length > 1 && !fullPath.startsWith('/login')) {
                sessionStorage.setItem('VOICIFY_POST_LOGIN_REDIRECT', fullPath);
            }
            window.location.href = `${window.location.origin.toString()}/login`;
        } else if (timeUntilExpiry <= thirtyMinutesInMs) {
            // Within 30 minutes of expiry, try to refresh
            this.refreshToken();
        } else {
            // Also check if the access token is expired or about to expire
            const storedAccessExpiration = localStorage.getItem(VOICIFY_ACCESS_EXPIRATION);
            if (storedAccessExpiration) {
                const accessExpiration = new Date(storedAccessExpiration);
                const fiveMinutesInMs = 5 * 60 * 1000;
                const timeUntilAccessExpiry = accessExpiration.getTime() - now.getTime();
                if (timeUntilAccessExpiry <= fiveMinutesInMs) {
                    // Access token expired or about to expire, refresh it
                    this.refreshToken();
                }
            }
        }
    }

    setRefreshTimer() {
        clearInterval(this.timerTaskId);
        this.checkRefreshTokenExpiry();
        // Check every 5 minutes 
        this.timerTaskId = setInterval(this.checkRefreshTokenExpiry.bind(this), 300000);
    }
    removeRefreshTimer() {
        clearInterval(this.timerTaskId);
    }

    cleanup() {
        // Clean up event listeners when container is destroyed
        this.removeRefreshTimer();
        if (this.storageListener) {
            window.removeEventListener('storage', this.storageListener);
        }
    }
    refreshToken(): Promise<IResult<AuthenticationResponse>> {
        var email = localStorage.getItem(VOICIFY_EMAIL_STORE);
        var refreshToken = localStorage.getItem(VOICIFY_REFRESH_TOKEN_STORE);
        if (refreshToken?.length) {
            this.setState({
                ...this.state,
                isLoading: true
            })
            var promise =
                voicifyApi.refreshToken({
                    email,
                    refreshToken
                });
            promise.then((authResult) => {

                if (authResult.resultType == "Ok") {
                    this.setState({
                        ...this.state,
                        isAuthenticated: true,
                        accessToken: authResult.data.accessToken,
                        refreshToken: authResult.data.refreshToken,
                        expirationDate: new Date(authResult.data.accessTokenExpirationDate),
                        refreshTokenExpirationDate: new Date(authResult.data.refreshTokenExpirationDate),
                        errors: [],
                        isLoading: false
                    });
                    this.updateLocalStorageAuth(email, authResult.data);
                }
                else {
                    this.setState({
                        ...this.state,
                        errors: authResult.errors,
                        isLoading: false
                    });
                    this.signOut();
                    const fullPath = window.location.pathname + window.location.search;
                    if (fullPath.length > 1 && !fullPath.startsWith('/login')) {
                        sessionStorage.setItem('VOICIFY_POST_LOGIN_REDIRECT', fullPath);
                    }
                    window.location.href = `${window.location.origin.toString()}/login`;
                }
            }).catch(error => {
                this.setState({
                    ...this.state,
                    errors: [...error],
                    isLoading: false
                });

            });
            return promise;
        }
    }

    signOut() {
        // Preserve the last used org tagged to the current user so auto-selection
        // can restore it if the same user signs back in.
        const currentOrg = localStorage.getItem('CURRENT_ORG');
        const currentEmail = localStorage.getItem(VOICIFY_EMAIL_STORE);
        localStorage.clear();
        if (currentOrg && currentEmail) {
            localStorage.setItem('VOICIFY_LAST_ORG_BY_USER', JSON.stringify({ email: currentEmail, org: currentOrg }));
        }
        this.setState({
            ...this.state,
            isAuthenticated: false,
            accessToken: '',
            refreshToken: '',
            errors: []
        })
    }
}